import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowUp, LifeBuoy, Sparkles, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils'
import { useAppModals } from '@/contexts/app-modals-context'
import { useSupportChat } from '@/hooks/useSupportChat'

interface SupportChatPanelProps {
  open: boolean
  onClose: () => void
}

type SupportMessage = {
  _id: string
  role: 'user' | 'ai' | 'founder'
  authorName?: string
  content: string
  createdAt: number
}

const MARKDOWN_LINK_COMPONENTS = {
  a: ({ node: _node, ...props }: { node?: unknown } & React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
}

export function SupportChatPanel({ open, onClose }: SupportChatPanelProps) {
  const { signedIn, messages, needsHuman, agentBusy, sendMessage, isLoading } = useSupportChat(open)
  const { openAuth } = useAppModals()

  const [input, setInput] = useState('')
  const [visible, setVisible] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const lastCountRef = useRef(0)

  // Drive the slide-in transition.
  useEffect(() => {
    if (open) requestAnimationFrame(() => setVisible(true))
    else setVisible(false)
  }, [open])

  // Esc closes; focus the composer on open.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const t = setTimeout(() => inputRef.current?.focus(), 150)
    return () => {
      window.removeEventListener('keydown', onKey)
      clearTimeout(t)
    }
  }, [open, onClose])

  // Auto-scroll to bottom as messages stream in (AI replies arrive live).
  useEffect(() => {
    if (!open) return
    if (messages.length === lastCountRef.current) return
    lastCountRef.current = messages.length
    const c = scrollContainerRef.current
    if (c) setTimeout(() => c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' }), 50)
  }, [messages.length, open])

  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text || agentBusy) return
    setInput('')
    const ta = inputRef.current
    if (ta) ta.style.height = 'auto'
    await sendMessage(text)
  }, [input, agentBusy, sendMessage])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/20 transition-opacity duration-200',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
      />

      {/* Right-side slide-in panel */}
      <div
        role="dialog"
        aria-label="Support chat"
        className={cn(
          'fixed inset-y-0 right-0 z-50 w-full max-w-md flex flex-col bg-card border-l border-border shadow-[0_0_40px_-12px_rgba(0,0,0,0.25)]',
          'transition-transform duration-200 ease-out',
          visible ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <LifeBuoy className="h-[18px] w-[18px] text-foreground stroke-[1.6]" />
            <span className="font-semibold text-sm text-foreground">Support</span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close support"
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Signed-out gate */}
        {!signedIn && !isLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <LifeBuoy className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-1.5">Sign in to chat with us</h3>
            <p className="text-sm text-muted-foreground mb-5 max-w-[260px]">
              We'll help you out and keep your conversation in one place.
            </p>
            <button
              onClick={() => openAuth('login')}
              className="px-5 py-2.5 bg-foreground text-background text-sm font-medium rounded-lg hover:bg-foreground/90 transition-colors"
            >
              Sign in
            </button>
          </div>
        ) : (
          <>
            {/* Messages */}
            <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
              {messages.length === 0 && !isLoading && (
                <div className="flex flex-col items-center text-center pt-10 px-4">
                  <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-3">
                    <Sparkles className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground max-w-[280px] leading-relaxed">
                    Hi! Tell us what's going wrong and we'll get it sorted — the more detail, the better.
                  </p>
                </div>
              )}

              {(messages as SupportMessage[]).map((msg) => (
                <SupportBubble key={msg._id} msg={msg} />
              ))}

              {needsHuman && messages.length > 0 && (
                <div className="flex justify-center pt-1">
                  <span className="text-[11px] text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
                    A teammate has been looped in — we'll reply here soon.
                  </span>
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="px-4 pb-4 pt-2 border-t border-border flex-shrink-0">
              <div className="flex items-end gap-2 bg-muted border border-border rounded-xl px-3 py-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Describe what's wrong…"
                  aria-label="Message support"
                  rows={1}
                  className="flex-1 bg-transparent text-sm text-foreground resize-none outline-none placeholder:text-muted-foreground max-h-32 min-h-[24px]"
                  style={{ height: 'auto', overflow: 'hidden' }}
                  onInput={(e) => {
                    const t = e.currentTarget
                    t.style.height = 'auto'
                    t.style.height = Math.min(t.scrollHeight, 128) + 'px'
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || agentBusy}
                  aria-label="Send message"
                  className={cn(
                    'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors',
                    input.trim() && !agentBusy
                      ? 'bg-foreground text-background hover:bg-foreground/90'
                      : 'bg-border text-muted-foreground',
                  )}
                >
                  <ArrowUp size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}

function SupportBubble({ msg }: { msg: SupportMessage }) {
  const isUser = msg.role === 'user'
  const time = useMemo(
    () => new Date(msg.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    [msg.createdAt],
  )

  if (isUser) {
    return (
      <div className="flex flex-col items-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-foreground text-background px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words">
          {msg.content}
        </div>
        <span className="text-[10px] text-muted-foreground mt-1 mr-1">{time}</span>
      </div>
    )
  }

  const isFounder = msg.role === 'founder'
  const label = isFounder ? msg.authorName || 'Footage' : 'Footage Support'

  return (
    <div className="flex flex-col items-start">
      <div className="flex items-center gap-1.5 mb-1 ml-0.5">
        <div className="w-5 h-5 rounded-full bg-muted ring-1 ring-border flex items-center justify-center overflow-hidden">
          {isFounder ? (
            <span className="text-[10px] font-semibold text-foreground">{label[0]?.toUpperCase()}</span>
          ) : (
            <Sparkles className="h-3 w-3 text-muted-foreground" />
          )}
        </div>
        <span className="text-xs font-semibold text-foreground">{label}</span>
        {isFounder && (
          <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
            Team
          </span>
        )}
        <span className="text-[10px] text-muted-foreground">{time}</span>
      </div>
      <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-muted text-foreground px-3.5 py-2 text-sm leading-relaxed">
        <div className="prose prose-sm prose-neutral max-w-none dark:prose-invert [&_p]:my-0 [&_p+p]:mt-2 [&_a]:text-blue-600 [&_a]:underline [&_a]:decoration-blue-300 [&_code]:bg-background/60 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs [&_ul]:my-1 [&_ol]:my-1">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_LINK_COMPONENTS}>
            {msg.content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  )
}
